import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { CarrierValidationComponent } from '@features/activity-types/upgrade-device/carrier-validation/carrier-validation.component';
import { AssetSelectionComponent } from '@features/shopping/asset/selection/asset-selection.component';
import { UserAsset } from '@models/dashboard.model';
import { calculateItemsToDelete } from '@models/functions.model';
import { ShoppingCartData, ShoppingCartItem } from '@models/shopping-cart.model';
import {
  CART_ITEM_TYPE_DEVICE,
  OrderTypeSettings,
  PreFlight,
  ValidateFields
} from '@models/shopping.model';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityManagerService } from '@services/activity/manager/activity-manager.service';
import { ActivityStateService } from '@services/activity/state/activity-state.service';
import { ModalService } from '@services/modal/modal.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { Subscription } from 'rxjs';

import { ConfirmUpgradeEligibleComponent } from '../confirm-upgrade-eligible/confirm-upgrade-eligible.component';
import { PreservePlansFeaturesComponent } from '../preserve-plans-features/preserve-plans-features.component';

@Component({
  selector: 'ts-activity-2-device-selection',
  templateUrl: './device-selection.component.html',
  imports: [CommonModule, TranslationPipe, AssetSelectionComponent, ConfirmUpgradeEligibleComponent]
})
export class DeviceSelectionComponent implements OnInit, OnDestroy {
  @Input() asset: UserAsset;
  @ViewChild('confirmUpgradeEligibleModal') confirm_upgrade_eligible_template: TemplateRef<any>;
  active_asset: UserAsset;
  shopping_cart: ShoppingCartData;
  existing_asset: UserAsset | undefined;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private activity_manager_svc: ActivityManagerService,
    private activity_state_svc: ActivityStateService,
    private modal_svc: ModalService,
    private shopping_svc: ShoppingService,
    private translation_svc: TranslationService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.shopping_svc.getShoppingItems().subscribe({
        next: (res: ShoppingCartData) => {
          this.shopping_cart = res;
          this.existing_asset = res.existing_asset;
        },
        error: (err: HttpErrorResponse) => {}
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  selectAsset(asset: UserAsset): void {
    this.active_asset = asset;

    const payload: PreFlight = {
      orderTypeId: this.activity_state_svc.orderTypeId()!,
      country_id: this.active_asset.line.country_id,
      lineId: this.active_asset.line.id
    };

    this.subscriptions.add(
      this.shopping_svc.carrierValidation(payload).subscribe({
        next: (res: ValidateFields) => {
          if (!res.valid && res.error_messages) {
            const modalRef: NgbModalRef = this.modal_svc.open(CarrierValidationComponent, {
              backdrop: 'static',
              centered: true,
              size: 'md'
            });
            modalRef.componentInstance.modal_header =
              this.translation_svc.translate('upgrade_device');
            modalRef.componentInstance.modal_body = res.error_messages[0].error_message[0];
          } else {
            const upgrade_eligible_date: Date = new Date(asset.line.upgrade_eligible_date!);
            const date: Date = new Date();

            if (date.getTime() <= upgrade_eligible_date.getTime()) {
              this.openConfirmationModal();
            } else {
              this.continueToUpgradeDevice(true);
            }
          }
        }
      })
    );
  }

  continueToUpgradeDevice(confirm: boolean): void {
    const order_type_id: number = this.activity_state_svc.orderTypeId()!;

    if (confirm) {
      this.modal_svc.dismissAll();

      if (
        this.existing_asset &&
        this.existing_asset.device.id !== this.active_asset.device.id &&
        this.shopping_cart.shopping_cart_items.length > 0
      ) {
        let modal_strings: string[] = this.translation_svc.getTranslatedModalLabels();

        this.modal_svc.confirmationDialog(
          {
            title: modal_strings[0],
            message: modal_strings[1],
            confirm_button_text: this.translation_svc.translate('continue'),
            cancel_button_text: this.translation_svc.translate('cancel'),
            onConfirm: () => {
              this.subscriptions.add(
                this.shopping_svc
                  .clearSpecifiedCartItems(
                    calculateItemsToDelete(this.shopping_cart.shopping_cart_items),
                    true,
                    'global'
                  )
                  .subscribe({
                    next: (res: ShoppingCartData) => {
                      this.shopping_cart.shopping_cart_items = res.shopping_cart_items;
                      this.getPlanSettingsCheck(order_type_id);
                    },
                    error: (err: HttpErrorResponse) => {}
                  })
              );
            }
          },
          {
            size: 'dialog-centered',
            centered: true,
            backdrop: 'static'
          }
        );
      } else this.getPlanSettingsCheck(order_type_id);
    }
  }

  navigateToDeviceList(keep_plan: boolean): void {
    this.activity_state_svc.setCountry({ id: this.active_asset.line.country_id, name: '' });
    this.activity_state_svc.setParameter('carrier_id', this.active_asset.line.carrier_id);
    this.activity_state_svc.setParameter('device_type', this.active_asset.device.type);
    this.activity_state_svc.setParameter(
      'device_catalog_id',
      this.active_asset.device.device_catalog_id
    );
    this.activity_state_svc.setParameter('lineId', this.active_asset.line.id);
    this.activity_state_svc.setParameter('existing_asset', this.active_asset);
    this.activity_state_svc.setParameter('keep_plan', keep_plan);
    this.activity_state_svc.saveConfig();

    if (
      this.existing_asset?.device.id &&
      this.active_asset.device.id != this.existing_asset?.device.id
    )
      this.shopping_cart.device_include_admin_options =
        this.shopping_cart.accessories_include_admin_options = undefined;

    const cart_payload: ShoppingCartData = {
      ...this.shopping_cart,
      country_id: this.active_asset.line.country_id,
      carrier_id: this.active_asset.line.carrier_id,
      device_type: this.active_asset.device.type,
      device_catalog_id: this.active_asset.device.device_catalog_id,
      orderTypeId: this.activity_state_svc.orderTypeId()!,
      lost_stolen: this.activity_state_svc.parameter('lost_stolen'),
      order_type_settings: this.activity_state_svc.parameter('order_type_settings'),
      order_for_person: this.shopping_cart.order_for_person,
      shopping_cart_items: this.shopping_cart.shopping_cart_items ?? [],
      lineId: this.active_asset.line.id,
      keep_plan: keep_plan,
      fields: this.shopping_cart.fields ?? [],
      existing_asset: {
        ...this.active_asset,
        asset_from_inventory: true,
        asset_selection_case: 1
      },
      progress_stepper: this.activity_manager_svc
        .state()
        .currentStep(this.activity_manager_svc.state().activeStepIndex())
    };

    this.subscriptions.add(
      this.shopping_svc.sendShoppingItems(cart_payload, true, 'global').subscribe({
        next: (res: ShoppingCartData) => {
          this.modal_svc.dismissAll();
          this.activity_manager_svc.navigateToNextStep({
            queryParams: {
              keep_plan: keep_plan
            }
          });
        },
        error: (err: HttpErrorResponse) => {}
      })
    );
  }

  private openConfirmationModal(): void {
    this.modal_svc.open(this.confirm_upgrade_eligible_template, {
      backdrop: 'static',
      centered: true,
      size: 'md'
    });
  }

  private openPreservePlanFeaturesModal(): void {
    const modalRef: NgbModalRef = this.modal_svc.open(PreservePlansFeaturesComponent, {
      backdrop: 'static',
      centered: true,
      size: 'md'
    });
    modalRef.componentInstance.shopping_cart = this.shopping_cart;
    modalRef.componentInstance.active_asset = this.active_asset;
    this.subscriptions.add(
      modalRef.componentInstance.selectKeepPlan.subscribe((val: boolean) => {
        this.selectedKeepPlan(val);
      })
    );
  }

  private getPlanSettingsCheck(order_type_id: number): void {
    this.subscriptions.add(
      this.shopping_svc.getPlanSettings(order_type_id).subscribe({
        next: (res: OrderTypeSettings) => {
          this.activity_state_svc.setParameter('order_type_settings', res);

          if (res.can_keep_plan) {
            this.modal_svc.dismissAll();
            this.openPreservePlanFeaturesModal();
          } else this.navigateToDeviceList(false);
        },
        error: (err: HttpErrorResponse) => {}
      })
    );
  }

  private selectedKeepPlan(event: boolean): void {
    if (
      this.shopping_cart.shopping_cart_items.find(
        (cart_item: ShoppingCartItem) => cart_item.type === CART_ITEM_TYPE_DEVICE
      ) &&
      this.shopping_cart.keep_plan !== event
    ) {
      this.modal_svc.dismissAll();
      let modal_strings: string[] = this.translation_svc.getTranslatedModalLabels();

      this.modal_svc.confirmationDialog(
        {
          title: modal_strings[0],
          message: modal_strings[1],
          confirm_button_text: this.translation_svc.translate('continue'),
          cancel_button_text: this.translation_svc.translate('cancel'),
          onConfirm: () => {
            this.subscriptions.add(
              this.shopping_svc
                .clearSpecifiedCartItems(
                  calculateItemsToDelete(this.shopping_cart.shopping_cart_items),
                  true,
                  'global'
                )
                .subscribe({
                  next: (res: ShoppingCartData) => {
                    this.shopping_cart.shopping_cart_items = res.shopping_cart_items;
                    this.navigateToDeviceList(event);
                  },
                  error: (err: HttpErrorResponse) => {}
                })
            );
          },
          onCancel: () => {
            this.openPreservePlanFeaturesModal();
          }
        },
        {
          backdrop: 'static',
          centered: true,
          size: 'md'
        }
      );
    } else this.navigateToDeviceList(event);
  }
}
